import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintIcon from '@mui/icons-material/Print';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { useDocumentsScreen } from '../hooks/useDocumentsScreen';
import DocumentListDialogs from './documentListDialogs';
import { hasSignedVersions, signatureStatus } from './documentSignatureStatus';

export default function DocumentListMobile() {
  const { patientId } = useParams();
  const screen = useDocumentsScreen(patientId);
  const { documents, isBusy, messages, editor, deletion, printDocument, signature } = screen;

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuDocument, setMenuDocument] = useState(null);

  const openMenu = (event, document) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuDocument(document);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuDocument(null);
  };
  const runMenuAction = (action) => {
    const document = menuDocument;
    closeMenu();
    action(document);
  };

  return (
    <>
      <Loading isToHidden={isBusy === false} />
      {messages.showSuccessMessage && (
        <SuccessMessage
          message={messages.successMessage}
          show={messages.showSuccessMessage}
          setShow={messages.setShowSuccessMessage}
        />
      )}
      {messages.showErrorMessage && (
        <ErrorMessage
          message={messages.errorMessage}
          show={messages.showErrorMessage}
          setShow={messages.setShowErrorMessage}
        />
      )}

      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Documentos
      </Typography>

      {documents.length === 0 ? (
        <Typography align="center" sx={{ py: 4 }}>
          Nenhum documento cadastrado ainda.
        </Typography>
      ) : (
        <Card variant="outlined">
          <List disablePadding>
            {documents.map((document) => (
              <React.Fragment key={document.id}>
                <ListItem
                  divider={!hasSignedVersions(document)}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="mais ações"
                      onClick={(event) => openMenu(event, document)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton onClick={() => editor.openDocument(document.id)} sx={{ pr: 7 }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography sx={{ fontWeight: 'bold' }}>{document.title}</Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Criado por: {document.createdBy || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Atualizado em: {document.updatedAt || document.createdAt || '-'}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={signatureStatus(document).color}
                            icon={
                              document.currentVersionSigned ? (
                                <VerifiedUserOutlinedIcon />
                              ) : undefined
                            }
                            label={signatureStatus(document).label}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {hasSignedVersions(document) && (
                  <Box sx={{ pl: 3, pr: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                    {document.signedVersions.map((version) => (
                      <Button
                        key={version.id}
                        fullWidth
                        size="small"
                        color="success"
                        startIcon={<PictureAsPdfOutlinedIcon />}
                        onClick={() => signature.openSignedVersion(document, version)}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        Versão {version.version} assinada em {version.signedAt}
                      </Button>
                    ))}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={() => runMenuAction((d) => editor.openDocument(d.id))}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={Boolean(menuDocument?.currentVersionSigned)}
          onClick={() => runMenuAction(signature.openSignModal)}
        >
          <ListItemIcon>
            <DrawOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Assinar digitalmente"
            secondary={menuDocument?.currentVersionSigned ? 'Versão atual já assinada' : null}
          />
        </MenuItem>
        <MenuItem onClick={() => runMenuAction((d) => printDocument(d.id))}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Imprimir</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={Boolean(menuDocument) && hasSignedVersions(menuDocument)}
          onClick={() => runMenuAction(deletion.handleDeleteClick)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Excluir"
            secondary={
              menuDocument && hasSignedVersions(menuDocument) ? 'Possui versões assinadas' : null
            }
          />
        </MenuItem>
      </Menu>

      <Fab
        color="primary"
        aria-label="novo documento"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={editor.openNewDocument}
      >
        <AddIcon />
      </Fab>

      <DocumentListDialogs patientId={patientId} screen={screen} />
    </>
  );
}
